// src/utils/azioniAcquisti.js
// Scritture del flusso acquisti NF → RdO → ODA (W436), le stesse da Centro Acquisti e dal
// dettaglio della NF. La logica pura (stati, totali, confronto) è in utils/acquisti.js e
// utils/rdo.js; qui solo il collegamento con il database e le Cloud Functions.
import { addDoc, collection, doc, getDocs, query, runTransaction, serverTimestamp, updateDoc, where, deleteDoc, writeBatch } from "firebase/firestore";
import { httpsCallable, getFunctions } from "firebase/functions";
import { db } from "../firebase";
import { odaDaNf, righeNfOrigine } from "./acquisti";
import { rdoDaNf, righeOdaDaRdo, noteOdaDaRdo, totaleOfferta, riepilogoRdo, rdoAperta } from "./rdo";
import { campiPresaInGestione } from "./azioniNF";
import { logModifica } from "./logModifica";
import { toLocalDateStr } from "./dateUtils";

/** Numero progressivo per anno e per azienda (ODA-2026-0007, RDO-2026-0003), in transazione. */
export async function prossimoNumero(orgId, sigla) {
  const anno = new Date().getFullYear();
  const ref = doc(db, "contatori", `${orgId}_${sigla.toLowerCase()}_${anno}`);
  return runTransaction(db, async (tx) => {
    const s = await tx.get(ref);
    const n = (s.exists() ? (s.data().ultimo || 0) : 0) + 1;
    tx.set(ref, { ultimo: n, orgId, anno }, { merge: true });
    return `${sigla.toUpperCase()}-${anno}-${String(n).padStart(4, "0")}`;
  });
}

const avvisa = async (nf, orgId, utente, titolo, testo) => {
  if (!nf?.createdBy || nf.createdBy === utente?.uid) return;
  try { await addDoc(collection(db, "notifiche"), { userId: nf.createdBy, orgId, tipo: "nf_presa_in_carico", titolo, testo, letta: false, createdAt: serverTimestamp() }); } catch { /* non blocca */ }
};

/** Le RdO di una NF. */
export async function rdoDellaNf(nfId, orgId) {
  const s = await getDocs(query(collection(db, "richiesteOfferta"), where("organizationId", "==", orgId), where("nfId", "==", nfId)));
  return s.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** Riepilogo delle RdO scritto sulla NF, per chi l'ha richiesta (senza prezzi). */
export async function aggiornaRiepilogoNf(nfId, orgId) {
  try {
    const tutte = await rdoDellaNf(nfId, orgId);
    await updateDoc(doc(db, "nuoveForniture", nfId), { rdoRiepilogo: riepilogoRdo(tutte), rdoAperte: tutte.filter(rdoAperta).length, updatedAt: serverTimestamp() });
  } catch (e) { console.warn("Riepilogo RdO non aggiornato:", e); }
}

// ── ODA ─────────────────────────────────────────────────────────────────────
/**
 * Crea l'ODA dalla NF (una NF = un ODA valido). Con `rdo` l'ordine nasce dall'offerta scelta:
 * fornitore e prezzi del fornitore; senza, dai dati della NF (prezzi indicativi). Restituisce
 * { id, numero, esistente }: se c'è già un ordine valido non ne crea un secondo.
 */
export async function creaOdaDaNf(nf, { orgId, utente, rdo = null }) {
  const gia = await getDocs(query(collection(db, "ordiniAcquisto"), where("nfId", "==", nf.id), where("organizationId", "==", orgId)));
  const valido = gia.docs.find(d => !d.data().eliminato && d.data().stato !== "Annullato");
  if (valido) return { id: valido.id, numero: valido.data().numeroODA, esistente: true };
  if (nf.annullata) throw new Error("La NF è annullata: non si può ordinare");
  const numeroODA = await prossimoNumero(orgId, "ODA");
  const dati = odaDaNf(nf, { numeroODA, orgId, uid: utente?.uid, oggi: toLocalDateStr(new Date()) });
  if (rdo) {
    const righeNf = righeNfOrigine(nf);
    Object.assign(dati, {
      fornitoreId: rdo.fornitoreId || "", fornitoreNome: rdo.fornitoreNome || "",
      righe: righeOdaDaRdo(rdo, righeNf), righeNf,
      note: [noteOdaDaRdo(rdo), dati.note].filter(Boolean).join(" · "),
      rdoId: rdo.id, rdoNumero: rdo.numero || "", offertaTotale: totaleOfferta(rdo),
      allegatoOfferta: rdo.offerta?.allegato || dati.allegatoOfferta || null,
    });
  }
  const ref = await addDoc(collection(db, "ordiniAcquisto"), { ...dati, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  await updateDoc(doc(db, "nuoveForniture", nf.id), { odaId: ref.id, odaNumero: numeroODA, odaStato: "Bozza", odaDifferenze: null, ...campiPresaInGestione(nf, utente), updatedAt: serverTimestamp() });
  if (rdo) await updateDoc(doc(db, "richiesteOfferta", rdo.id), { odaId: ref.id, odaNumero: numeroODA, updatedAt: serverTimestamp() });
  await avvisa(nf, orgId, utente, "Ordine in preparazione per la tua NF",
    `${utente?.displayName || "L'ufficio Acquisti"} ha creato l'ordine ${numeroODA} per la NF #${nf.numero || ""}${rdo ? ` (fornitore ${rdo.fornitoreNome})` : ""}`);
  await logModifica({ orgId, uid: utente?.uid || "", nomeUtente: utente?.displayName || "", sezione: "ordiniAcquisto", refId: ref.id,
    refNome: `NF #${nf.numero || ""}`, campo: "ordineCreato", campoLabel: rdo ? `ODA creato dall'offerta ${rdo.numero || ""}` : "ODA creato dalla NF", before: "—", after: numeroODA });
  return { id: ref.id, numero: numeroODA, esistente: false };
}

// ── RdO ─────────────────────────────────────────────────────────────────────
/** Prepara una RdO per ciascun fornitore scelto (stato «Da inviare», senza numero). Salta chi ne ha già una aperta. */
export async function creaRdo(nf, fornitori, { orgId, utente }) {
  const esistenti = await rdoDellaNf(nf.id, orgId);
  const nuove = [];
  for (const f of fornitori) {
    if (esistenti.some(r => r.fornitoreId === f.id && r.stato !== "Annullata" && r.stato !== "Scartata")) continue;
    const dati = rdoDaNf(nf, f, { orgId, uid: utente?.uid, nome: utente?.displayName });
    const ref = await addDoc(collection(db, "richiesteOfferta"), { ...dati, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    nuove.push({ id: ref.id, ...dati });
  }
  if (!nf.presaInCaricoDa) await updateDoc(doc(db, "nuoveForniture", nf.id), { ...campiPresaInGestione(nf, utente), updatedAt: serverTimestamp() });
  await aggiornaRiepilogoNf(nf.id, orgId);
  if (nuove.length) await avvisa(nf, orgId, utente, "Richieste d'offerta per la tua NF",
    `${utente?.displayName || "L'ufficio Acquisti"} sta chiedendo le offerte a ${nuove.length} fornitor${nuove.length === 1 ? "e" : "i"} per la NF #${nf.numero || ""}`);
  return nuove;
}

/** Invio per email dal server (numero assegnato al primo invio). */
export async function inviaRdoEmail(rdo, { sollecito = false } = {}) {
  const fn = httpsCallable(getFunctions(), "inviaEmailRdO");
  const r = await fn({ rdoId: rdo.id, sollecito });
  await aggiornaRiepilogoNf(rdo.nfId, rdo.organizationId);
  return r?.data || {};
}

/** Richiesta mandata a mano (telefono, email propria, PEC): si segna inviata e prende il suo numero. */
export async function segnaRdoInviata(rdo, utente) {
  const numero = rdo.numero || await prossimoNumero(rdo.organizationId, "RDO");
  await updateDoc(doc(db, "richiesteOfferta", rdo.id), { numero, stato: "Inviata", inviataAt: serverTimestamp(), inviataDa: utente?.uid || "", inviataDaNome: utente?.displayName || "", emailInviata: false, updatedAt: serverTimestamp() });
  await aggiornaRiepilogoNf(rdo.nfId, rdo.organizationId);
  return numero;
}

/** Offerta ricevuta dal fornitore (prezzi per riga o totale, condizioni, PDF). */
export async function salvaOfferta(rdo, offerta, utente) {
  const numero = rdo.numero || await prossimoNumero(rdo.organizationId, "RDO");
  await updateDoc(doc(db, "richiesteOfferta", rdo.id), {
    numero, stato: rdo.stato === "Scelta" ? "Scelta" : "Offerta ricevuta",
    offerta: { ...offerta, registrataDa: utente?.displayName || "" }, offertaTotale: totaleOfferta(rdo, offerta), offertaRicevutaAt: serverTimestamp(),
    ...(rdo.inviataAt ? {} : { inviataAt: serverTimestamp(), inviataDaNome: utente?.displayName || "" }),
    updatedAt: serverTimestamp(),
  });
  await aggiornaRiepilogoNf(rdo.nfId, rdo.organizationId);
}

/** Sceglie un'offerta: le altre ricevute diventano «Scartata», quelle senza risposta «Annullata». */
export async function scegliOfferta(scelta, tutte, utente) {
  const batch = writeBatch(db);
  tutte.forEach(r => {
    const ref = doc(db, "richiesteOfferta", r.id);
    if (r.id === scelta.id) batch.update(ref, { stato: "Scelta", sceltaDa: utente?.displayName || "", sceltaAt: serverTimestamp(), updatedAt: serverTimestamp() });
    else if (r.stato === "Offerta ricevuta" || r.stato === "Scelta") batch.update(ref, { stato: "Scartata", motivoScarto: `Scelta l'offerta di ${scelta.fornitoreNome}`, updatedAt: serverTimestamp() });
    else if (r.stato === "Da inviare" || r.stato === "Inviata") batch.update(ref, { stato: "Annullata", motivoScarto: `Scelta l'offerta di ${scelta.fornitoreNome} prima della risposta`, updatedAt: serverTimestamp() });
  });
  await batch.commit();
  await aggiornaRiepilogoNf(scelta.nfId, scelta.organizationId);
}

export async function scartaRdo(rdo, motivo, utente) {
  await updateDoc(doc(db, "richiesteOfferta", rdo.id), { stato: rdo.stato === "Offerta ricevuta" ? "Scartata" : "Annullata", motivoScarto: String(motivo || "").trim(), scartataDa: utente?.displayName || "", updatedAt: serverTimestamp() });
  await aggiornaRiepilogoNf(rdo.nfId, rdo.organizationId);
}

/** Solo le bozze (mai inviate, senza numero) si cancellano. */
export async function eliminaRdo(rdo) {
  if (rdo.stato !== "Da inviare") throw new Error("Una richiesta già inviata non si elimina: si annulla");
  await deleteDoc(doc(db, "richiesteOfferta", rdo.id));
  await aggiornaRiepilogoNf(rdo.nfId, rdo.organizationId);
}

/** NF annullata: le sue RdO ancora aperte si annullano con lo stesso motivo. */
export async function annullaRdoDellaNf(nf, orgId, motivo) {
  const aperte = (await rdoDellaNf(nf.id, orgId)).filter(rdoAperta);
  if (!aperte.length) return 0;
  const batch = writeBatch(db);
  aperte.forEach(r => batch.update(doc(db, "richiesteOfferta", r.id), { stato: "Annullata", motivoScarto: `NF annullata: ${String(motivo || "").trim()}`, updatedAt: serverTimestamp() }));
  await batch.commit();
  return aperte.length;
}

